/**
 * EmployerCommandCentrePage.jsx
 * Employer Command Centre — operational dashboard at `/employer`.
 *
 * Mission
 * ───────
 * Answer, on first paint:
 *   • What requires attention?
 *   • What roles are active?
 *   • What reviews are pending?
 *   • Which reviewers are active?
 *   • What governance states need action?
 *
 * STRICT — operational honesty rules
 * ──────────────────────────────────
 *   • NO fake numbers. NO synthetic charts. NO placeholder KPIs.
 *   • Every metric is sourced from a real, existing endpoint:
 *       - /api/employer/v1/me      → identity + role
 *       - /api/employer/roles      → active roles count + recent list
 *       - /api/employer/v1/plan    → entitlement tier
 *       - Supabase RLS reads on:
 *           emp_candidate_reviews        (open reviews)
 *           emp_reviewer_assignments     (active reviewers)
 *           emp_member_invitations       (pending invites)
 *   • If any read fails → counter shows `—`, never `0` fabricated.
 *   • Empty states use prose ("No active roles yet.") not zero pills.
 *   • Reviewers (member.role === 'reviewer') are redirected to
 *     /employer/reviews so they see their queue, not an admin
 *     dashboard.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, RefreshCw, Briefcase, UserCheck, MailPlus, ClipboardList,
  ShieldCheck, AlertCircle, ArrowRight, Plus, Building2,
  Users as UsersIcon, Activity, AlertTriangle,
} from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { planLabel } from '@/lib/employerPlanLabels';
import { ROLE_LABEL } from '@/lib/employerAuthority';
import { isActiveRoleStatus } from '@/lib/roleLifecycle';
import { useAuth } from '@/contexts/AuthContext';
import { EmployerNoMembershipStub } from '@/components/employer/EmployerNoMembershipStub';
import HelpOrientationCard from '@/components/HelpOrientationCard';


function metricValue(v) {
  if (v === null || v === undefined) return '\u2014';
  return String(v);
}


function relativeTime(ts) {
  if (!ts) return null;
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60)    return 'just now';
  if (diffSec < 3600)  return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}


// ─── Top metric card ──────────────────────────────────────────────
function MetricCard({ label, value, hint, icon: Icon, tone = 'default', testid, to }) {
  const toneClass = {
    default: 'text-ndor-ink',
    warn:    'text-amber-400',
    alert:   'text-rose-400',
  }[tone];

  const inner = (
    <Card
      className="border-ndor-edge bg-ndor-surface hover:border-ndor-gold/30 transition-colors h-full"
      data-testid={testid}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <span className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-medium">
            {label}
          </span>
          {Icon && <Icon className="h-4 w-4 text-ndor-ink-muted shrink-0" />}
        </div>
        <div className={`text-3xl font-semibold font-mono-ui ${toneClass} mb-1`}>
          {value}
        </div>
        {hint && (
          <div className="text-[11px] text-ndor-ink-muted leading-relaxed">
            {hint}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
}


export default function EmployerCommandCentrePage() {
  const { profile } = useAuth();
  const navigate    = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  // F-D4 (RC 1.62) — calm empty stub when the user is
  // authenticated with employer_access but has no membership.
  const [noMembership, setNoMembership] = useState(false);

  const [me,         setMe]         = useState(null);
  const [rolesRaw,   setRolesRaw]   = useState(null); // null = unread; [] = empty
  const [openReviews, setOpenReviews] = useState(null);
  const [activeAssignments, setActiveAssignments] = useState(null);
  const [pendingInvites,    setPendingInvites]    = useState(null);
  const [plan, setPlan] = useState(null);

  // ─── Initial load ────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // 1) /v1/me up-front so we know the account_id + role
      const meData = await apiFetch('/api/employer/v1/me');
      setMe(meData);

      // Reviewer-only members are redirected — their canonical
      // landing is the reviewer queue, not an admin dashboard.
      const role = meData?.member?.role;
      if (role === 'reviewer') {
        navigate('/employer/reviews', { replace: true });
        return;
      }

      const accountId = meData?.organisation?.id || meData?.account_id;

      // 2) parallel fetch — every read degrades independently
      const settled = await Promise.allSettled([
        apiFetch('/api/employer/roles'),
        apiFetch('/api/employer/v1/plan'),
        accountId ? supabase
          .from('emp_candidate_reviews')
          .select('review_id, status, role_id, reviewer_user_id, candidate_user_id, created_at')
          .eq('account_id', accountId)
          .in('status', ['pending', 'in_review'])
          .order('created_at', { ascending: false })
          .limit(20) : Promise.resolve({ data: null }),
        accountId ? supabase
          .from('emp_reviewer_assignments')
          .select('assignment_id, reviewer_user_id, assignment_status, updated_at, role_id')
          .eq('account_id', accountId)
          .eq('assignment_status', 'active')
          .order('updated_at', { ascending: false }) : Promise.resolve({ data: null }),
        accountId ? supabase
          .from('emp_member_invitations')
          .select('id, status, invited_email, invited_role, created_at')
          .eq('account_id', accountId)
          .eq('status', 'pending')
          .order('created_at', { ascending: false }) : Promise.resolve({ data: null }),
      ]);

      // Roles
      if (settled[0].status === 'fulfilled') {
        const v = settled[0].value;
        const list = v?.items || v?.roles || (Array.isArray(v) ? v : []) || [];
        setRolesRaw(Array.isArray(list) ? list : []);
      } else {
        setRolesRaw(null);
      }
      // Plan
      if (settled[1].status === 'fulfilled') setPlan(settled[1].value || null);
      // Open reviews
      if (settled[2].status === 'fulfilled' && !settled[2].value?.error) {
        setOpenReviews(settled[2].value?.data || []);
      } else {
        setOpenReviews(null);
      }
      // Active assignments
      if (settled[3].status === 'fulfilled' && !settled[3].value?.error) {
        setActiveAssignments(settled[3].value?.data || []);
      } else {
        setActiveAssignments(null);
      }
      // Pending invites
      if (settled[4].status === 'fulfilled' && !settled[4].value?.error) {
        setPendingInvites(settled[4].value?.data || []);
      } else {
        setPendingInvites(null);
      }
    } catch (err) {
      if (err?.status === 403) {
        setError('Your account does not have employer workspace access.');
      } else if (err?.status === 404) {
        // F-D4 (RC 1.62 employer-auth doctrine) — no org yet.
        // The user is authenticated with employer_access=true,
        // but is not attached to any organisation. Render the
        // calm empty-workspace stub instead of redirecting to
        // the org-setup hub (which would expose creation flow
        // now locked behind PUBLIC_ORG_CREATION_ENABLED=false).
        setNoMembership(true);
        return;
      } else {
        setError(err?.message || 'Failed to load command centre.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // ─── Derived ─────────────────────────────────────────────────
  const firstName = profile?.first_name || me?.member?.first_name || '';
  const role      = me?.member?.role || null;
  const orgName   = me?.organisation?.name || null;

  // RC 1.61 BUG.A.2 FIX (operator-approved 2026-05-29):
  // Same canonical-alias doctrine as EmployerDashboardPanel — honours
  // both `'active'` and legacy `'live'` via the shared
  // `isActiveRoleStatus` helper. See `lib/roleLifecycle.js` for the
  // single source of truth.
  const activeRolesCount =
    Array.isArray(rolesRaw)
      ? rolesRaw.filter((r) => isActiveRoleStatus(r?.status)).length
      : null;

  const pendingDecisions = Array.isArray(openReviews) ? openReviews.length : null;
  const activeReviewers  = Array.isArray(activeAssignments)
    ? new Set(activeAssignments.map((a) => a.reviewer_user_id)).size
    : null;
  const pendingInviteCount = Array.isArray(pendingInvites) ? pendingInvites.length : null;

  // Governance alerts =
  //   pending invitations (need acceptance) + reviews in_review > 7d
  const stalledReviews = useMemo(() => {
    if (!Array.isArray(openReviews)) return null;
    const sevenDaysAgo = Date.now() - 7 * 86400_000;
    return openReviews.filter((r) =>
      r.status === 'in_review' && new Date(r.created_at).getTime() < sevenDaysAgo,
    );
  }, [openReviews]);

  const governanceAlerts = useMemo(() => {
    if (pendingInviteCount === null && stalledReviews === null) return null;
    return (pendingInviteCount ?? 0) + (stalledReviews?.length ?? 0);
  }, [pendingInviteCount, stalledReviews]);

  const recentRoles = useMemo(
    () => Array.isArray(rolesRaw) ? rolesRaw.slice(0, 5) : [],
    [rolesRaw],
  );

  const reviewsNeedingAttention = useMemo(() => {
    if (!Array.isArray(openReviews)) return [];
    return openReviews.slice(0, 6);
  }, [openReviews]);

  const reviewerActivity = useMemo(() => {
    if (!Array.isArray(activeAssignments)) return [];
    // Aggregate by reviewer
    const map = new Map();
    for (const a of activeAssignments) {
      const k = a.reviewer_user_id;
      const cur = map.get(k) || { reviewer_user_id: k, assigned: 0, last_updated: null };
      cur.assigned += 1;
      if (!cur.last_updated || (a.updated_at && a.updated_at > cur.last_updated)) {
        cur.last_updated = a.updated_at;
      }
      map.set(k, cur);
    }
    return Array.from(map.values()).slice(0, 5);
  }, [activeAssignments]);

  // ─── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-24"
        data-testid="employer-command-centre-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  // F-D4 (RC 1.62 employer-auth doctrine) — calm empty stub.
  if (noMembership) {
    return <EmployerNoMembershipStub />;
  }

  if (error) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-command-centre-error"
      >
        <AlertCircle className="mx-auto mb-4 h-10 w-10 text-rose-500" />
        <h1 className="text-xl font-semibold text-ndor-ink">Can&apos;t load command centre</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">{error}</p>
        <Button variant="outline" className="mt-6" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-7xl py-6 md:py-10 px-3 md:px-6 space-y-8 md:space-y-10"
      data-testid="employer-command-centre"
    >
      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="space-y-5">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="space-y-2 max-w-2xl">
            <p className="text-[10px] uppercase tracking-[0.24em] text-ndor-gold font-medium">
              Employer Command Centre
            </p>
            <h1 className="text-3xl md:text-4xl font-semibold text-ndor-ink leading-tight">
              {firstName ? <>Hello, <span className="text-ndor-gold">{firstName}</span>.</> : 'Welcome.'}
            </h1>
            <p className="text-sm text-ndor-ink-muted leading-relaxed">
              Manage your organisation, roles, reviewers and hiring decisions.
              {orgName && <> Operating as <span className="text-ndor-ink">{orgName}</span>.</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              data-testid="employer-command-centre-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Link to="/employer/roles/new" data-testid="employer-command-centre-create-role">
              <Button
                size="sm"
                className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create role
              </Button>
            </Link>
          </div>
        </div>

        {/* Identity strip */}
        <div className="flex items-center gap-3 text-[11px] flex-wrap">
          <Badge variant="outline" className="border-ndor-edge text-ndor-ink-secondary" data-testid="cc-role-chip">
            <ShieldCheck className="h-3 w-3 mr-1" />
            You are: {ROLE_LABEL[role] || role || '—'}
          </Badge>
          {plan && (
            <Badge variant="outline" className="border-ndor-edge text-ndor-ink-secondary" data-testid="cc-plan-chip">
              Plan: {planLabel(plan?.entitlement?.plan_tier || plan?.active_plan?.plan_tier)}
            </Badge>
          )}
          {/* RC 1.61 (operator-approved 2026-05-29): the
              "Free Beta · placeholder pricing" badge has been
              removed. Stripe billing is live and Starter pricing is
              real; the badge was a launch-period placeholder that
              became actively misleading. Do not re-introduce — if
              future pre-launch surfaces need a similar pill, gate
              it on a real signal (e.g. `entitlement.plan_tier ===
              'free'`) rather than rendering unconditionally. */}
        </div>
      </section>

      {/* ── Top metric row ─────────────────────────────────────── */}
      <section
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
        data-testid="cc-metric-row"
      >
        <MetricCard
          label="Active Roles"
          value={metricValue(activeRolesCount)}
          hint={activeRolesCount === 0 ? 'No active roles yet.' : 'Currently published.'}
          icon={Briefcase}
          testid="cc-metric-active-roles"
          to="/employer/roles"
        />
        <MetricCard
          label="Pending Decisions"
          value={metricValue(pendingDecisions)}
          hint={pendingDecisions === 0 ? 'No reviews awaiting decision.' : 'Across all reviewers.'}
          icon={ClipboardList}
          tone={pendingDecisions > 0 ? 'warn' : 'default'}
          testid="cc-metric-pending-decisions"
        />
        <MetricCard
          label="Active Reviewers"
          value={metricValue(activeReviewers)}
          hint={activeReviewers === 0 ? 'No reviewers assigned yet.' : 'With active assignments.'}
          icon={UserCheck}
          testid="cc-metric-active-reviewers"
          to="/employer/reviewers"
        />
        <MetricCard
          label="Governance Alerts"
          value={metricValue(governanceAlerts)}
          hint={
            governanceAlerts === 0
              ? 'Nothing needs attention.'
              : `${pendingInviteCount ?? 0} pending invite${(pendingInviteCount ?? 0) === 1 ? '' : 's'} · ${stalledReviews?.length ?? 0} stalled review${(stalledReviews?.length ?? 0) === 1 ? '' : 's'}`
          }
          icon={AlertTriangle}
          tone={governanceAlerts > 0 ? 'alert' : 'default'}
          testid="cc-metric-governance-alerts"
        />
      </section>

      {/* ── Main sections — 2-column on lg+ ────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* A. Recent Roles */}
        <Card className="border-ndor-edge bg-ndor-surface lg:col-span-2" data-testid="cc-section-recent-roles">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-ndor-ink-muted" />
              Recent roles
            </CardTitle>
            <Link
              to="/employer/roles"
              className="text-xs text-ndor-ink-muted hover:text-ndor-ink inline-flex items-center gap-1"
              data-testid="cc-recent-roles-viewall"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {rolesRaw === null && (
              <p className="text-xs text-ndor-ink-muted">Roles list unavailable — retry.</p>
            )}
            {Array.isArray(rolesRaw) && rolesRaw.length === 0 && (
              <div className="py-8 text-center" data-testid="cc-recent-roles-empty">
                <Briefcase className="mx-auto mb-3 h-8 w-8 text-ndor-ink-muted opacity-50" />
                <p className="text-sm text-ndor-ink">No roles yet.</p>
                <p className="text-xs text-ndor-ink-muted mt-1">
                  Create your first role to begin matching candidates.
                </p>
                <Link to="/employer/roles/new">
                  <Button size="sm" variant="outline" className="mt-4">
                    <Plus className="mr-2 h-3 w-3" /> Create role
                  </Button>
                </Link>
              </div>
            )}
            {Array.isArray(rolesRaw) && rolesRaw.length > 0 && (
              <ul className="divide-y divide-ndor-edge -mx-2" data-testid="cc-recent-roles-list">
                {recentRoles.map((r) => (
                  <li key={r.role_id || r.id} className="px-2 py-3">
                    <Link
                      to={`/employer/roles/${r.role_id || r.id}`}
                      className="flex items-start justify-between gap-3 hover:bg-ndor-surface-alt/40 -mx-2 px-2 py-1 rounded transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ndor-ink truncate">{r.title || 'Untitled role'}</p>
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-ndor-ink-muted flex-wrap">
                          <Badge variant="outline" className="border-ndor-edge text-[10px] uppercase tracking-wider">
                            {(r.status || 'draft')}
                          </Badge>
                          {r.updated_at && (
                            <span>Updated {relativeTime(r.updated_at)}</span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-ndor-ink-muted mt-1 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* B. Reviews Requiring Attention */}
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="cc-section-reviews-attention">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-ndor-ink-muted" />
              Reviews needing attention
            </CardTitle>
            <Link
              to="/employer/reviewers"
              className="text-xs text-ndor-ink-muted hover:text-ndor-ink inline-flex items-center gap-1"
              data-testid="cc-reviews-attention-manage"
            >
              Manage <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {openReviews === null && (
              <p className="text-xs text-ndor-ink-muted">Reviews unavailable — retry.</p>
            )}
            {Array.isArray(openReviews) && reviewsNeedingAttention.length === 0 && (
              <div className="py-6 text-center" data-testid="cc-reviews-attention-empty">
                <p className="text-sm text-ndor-ink">All clear.</p>
                <p className="text-xs text-ndor-ink-muted mt-1">
                  No reviews awaiting decision.
                </p>
              </div>
            )}
            {reviewsNeedingAttention.length > 0 && (
              <ul className="space-y-2.5" data-testid="cc-reviews-attention-list">
                {reviewsNeedingAttention.map((r) => {
                  const stalled = (stalledReviews || []).some(
                    (s) => s.review_id === r.review_id,
                  );
                  return (
                    <li
                      key={r.review_id}
                      className="flex items-start justify-between gap-2 py-2 border-b border-ndor-edge last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-mono-ui text-ndor-ink-muted">
                          REV-{String(r.review_id || '').slice(0, 8)}
                        </p>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <Badge
                            variant="outline"
                            className={
                              r.status === 'in_review'
                                ? 'border-amber-500/40 text-amber-400 text-[10px] uppercase tracking-wider'
                                : 'border-ndor-edge text-[10px] uppercase tracking-wider'
                            }
                          >
                            {r.status}
                          </Badge>
                          {stalled && (
                            <Badge className="bg-rose-500/15 text-rose-300 text-[10px] uppercase tracking-wider border-0">
                              Stalled &gt; 7d
                            </Badge>
                          )}
                        </div>
                      </div>
                      {r.created_at && (
                        <span className="text-[10px] text-ndor-ink-muted shrink-0">
                          {relativeTime(r.created_at)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* C. Reviewer Activity */}
        <Card className="border-ndor-edge bg-ndor-surface lg:col-span-2" data-testid="cc-section-reviewer-activity">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
              <Activity className="h-4 w-4 text-ndor-ink-muted" />
              Reviewer activity
            </CardTitle>
            <Link
              to="/employer/reviewers"
              className="text-xs text-ndor-ink-muted hover:text-ndor-ink inline-flex items-center gap-1"
              data-testid="cc-reviewer-activity-manage"
            >
              Manage reviewers <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {activeAssignments === null && (
              <p className="text-xs text-ndor-ink-muted">Reviewer data unavailable — retry.</p>
            )}
            {Array.isArray(activeAssignments) && reviewerActivity.length === 0 && (
              <div className="py-6 text-center" data-testid="cc-reviewer-activity-empty">
                <UsersIcon className="mx-auto mb-3 h-8 w-8 text-ndor-ink-muted opacity-50" />
                <p className="text-sm text-ndor-ink">No reviewers assigned yet.</p>
                <p className="text-xs text-ndor-ink-muted mt-1">
                  Invite reviewers from the Organisation hub.
                </p>
                <Link to="/employer/reviewers">
                  <Button size="sm" variant="outline" className="mt-4">
                    Manage reviewers
                  </Button>
                </Link>
              </div>
            )}
            {reviewerActivity.length > 0 && (
              <ul className="divide-y divide-ndor-edge -mx-2" data-testid="cc-reviewer-activity-list">
                {reviewerActivity.map((rev) => (
                  <li
                    key={rev.reviewer_user_id}
                    className="px-2 py-2.5 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-mono-ui text-ndor-ink-muted truncate">
                        REVIEWER-{String(rev.reviewer_user_id || '').slice(0, 8)}
                      </p>
                      <p className="text-[10px] text-ndor-ink-muted mt-0.5">
                        {rev.assigned} assignment{rev.assigned === 1 ? '' : 's'}
                        {rev.last_updated && ` · last activity ${relativeTime(rev.last_updated)}`}
                      </p>
                    </div>
                    <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-[10px] uppercase tracking-wider">
                      Active
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* D. Governance / Invitations */}
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="cc-section-governance">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-ndor-ink-muted" />
              Governance
            </CardTitle>
            <Link
              to="/employer/members"
              className="text-xs text-ndor-ink-muted hover:text-ndor-ink inline-flex items-center gap-1"
              data-testid="cc-governance-manage"
            >
              Members <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 py-2 border-b border-ndor-edge">
                <MailPlus className="h-4 w-4 text-ndor-ink-muted mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-ndor-ink">
                    Pending invitations
                  </p>
                  <p className="text-[11px] text-ndor-ink-muted">
                    {pendingInviteCount === null
                      ? 'Unavailable'
                      : pendingInviteCount === 0
                        ? 'No invitations awaiting acceptance.'
                        : `${pendingInviteCount} invitation${pendingInviteCount === 1 ? '' : 's'} awaiting acceptance.`}
                  </p>
                </div>
                <Badge variant="outline" className="border-ndor-edge text-[11px]">
                  {metricValue(pendingInviteCount)}
                </Badge>
              </li>
              <li className="flex items-start gap-2 py-2 border-b border-ndor-edge">
                <AlertTriangle className="h-4 w-4 text-ndor-ink-muted mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-ndor-ink">Stalled reviews ({'>'} 7d)</p>
                  <p className="text-[11px] text-ndor-ink-muted">
                    {stalledReviews === null
                      ? 'Unavailable'
                      : stalledReviews.length === 0
                        ? 'No reviewer is sitting on a review.'
                        : `${stalledReviews.length} review${stalledReviews.length === 1 ? '' : 's'} in-review for over a week.`}
                  </p>
                </div>
                <Badge variant="outline" className="border-ndor-edge text-[11px]">
                  {metricValue(stalledReviews?.length ?? null)}
                </Badge>
              </li>
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* RC 1.63.1 — Help & orientation stub. Calm rail with How It
          Works + Reviewer User Guide (all roles) + Employer User
          Guide (owner/admin only). Role-aware via `role`. */}
      <HelpOrientationCard workspace="employer" memberRole={role} />

      <footer
        className="text-[11px] text-ndor-ink-muted pt-4 border-t border-ndor-edge flex items-center justify-between flex-wrap gap-2"
        data-testid="cc-footer-honesty"
      >
        <span>
          Every counter is read from live records. Employer billing is not active yet.
        </span>
        <Link
          to="/employer/organisation"
          className="hover:text-ndor-ink inline-flex items-center gap-1"
        >
          <Building2 className="h-3 w-3" /> Configure organisation
        </Link>
      </footer>
    </div>
  );
}
